import * as React from "react";
import {
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import { ButtonDefinition } from "@kalabo/golfsavers-common";

// create  type prop for info, success, warning, error
export type AlertType = "info" | "success" | "warning" | "error";

interface IProps {
  title: string | React.ReactNode;
  description?: string;
  list?: string[];
  showIcon?: boolean;
  type?: AlertType;
  centered?: boolean;
  iconAlign?: "top" | "center";
  buttons?: ButtonDefinition[];
  className?: string;
  spacing?: "micro" | "tight" | "normal";
  children?: React.ReactNode;
}

const Alert = ({
  title,
  description,
  list,
  showIcon = true,
  type = "info",
  centered = false,
  iconAlign = "top",
  buttons,
  className = "mb-2",
  spacing = "normal",
  children,
}: IProps) => {
  const getPrimaryColor = () => {
    switch (type) {
      case "success":
        return "text-green";
      case "info":
        return "text-blue";
      case "warning":
        return "text-[#DA5B00]";
      case "error":
        return "text-red";
    }
  };

  const getSecondaryColor = () => {
    switch (type) {
      case "success":
        return "bg-[#f6ffed] border-[#b7eb8f] border-[1px]";
      case "info":
        return "bg-[#038ECF1A] ";
      case "warning":
        return "bg-[#fff1e6] border-[#ffe7ba] border-[1px]";
      case "error":
        return "bg-[#fff2f0] border-[#ffccc7] border-[1px]";
    }
  };

  const getPadding = () => {
    switch (spacing) {
      case "micro":
        return "py-1 px-2";
      case "tight":
        return "p-2";
      case "normal":
        return "p-3";
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case "success":
        return "bg-green";
      case "info":
        return "bg-blue ";
      case "warning":
        return "bg-darkorange";
      case "error":
        return "bg-red";
    }
  };

  const iconSize = () => {
    switch (spacing) {
      case "micro":
      case "tight":
        return "w-4 h-4";
      case "normal":
        return "w-5 h-5";
    }
  };

  const icon = () => {
    switch (type) {
      case "success":
        return (
          <CheckCircleIcon className={`${iconSize()} ${getPrimaryColor()}`} />
        );
      case "info":
      case "warning":
        return (
          <InformationCircleIcon
            className={`${iconSize()} ${getPrimaryColor()}`}
          />
        );
      case "error":
        return (
          <InformationCircleIcon
            className={`${iconSize()} ${getPrimaryColor()}`}
          />
        );
    }
  };

  return (
    <div
      className={`rounded-md w-full ${getPadding()} ${getSecondaryColor()} ${className}`}
    >
      <div className="flex items-center">
        {showIcon && (
          <div
            className={`flex-shrink-0
            ${iconAlign === "top" ? "self-start" : "self-center"}`}
          >
            {icon()}
          </div>
        )}
        <div className="ml-2 flex-1 md:flex md:justify-between">
          <div className={centered ? "text-center" : "text-left"}>
            <div
              className={`text-13 leading-[133%] font-medium ${getPrimaryColor()}`}
            >
              {title}
            </div>
            {description && (
              <div className={`mt-1 text-sm  ${getPrimaryColor()}`}>
                {description}
              </div>
            )}
            {list && (
              <ul className="mt-1 mb-0 text-sm list-disc list-inside">
                {list.map((item, idx) => (
                  <li key={idx} className={getPrimaryColor()}>
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div>
          <div className="inline-flex gap-2">
            {buttons?.map((button: ButtonDefinition, idx: any) => {
              return (
                <button
                  key={idx}
                  className={`rounded-md py-[3px] px-[10px] text-15 font-semibold text-white ${getButtonColor()}`}
                  onClick={button.onClick}
                >
                  {button.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

export default Alert;
